import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Eye,
  Loader2,
  Package,
  ImageIcon,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const PROVIDER_LOGOS = {
  woocommerce: "/integrations/woocommerce.svg",
  trendyol: "/integrations/trendyol.svg",
  hepsiburada: "/integrations/hepsiburada.svg",
  amazon: "/integrations/amazon.svg",
  n11: "/integrations/n11.png",
  idefix: "/integrations/idefix.svg",
  pazarama: "/integrations/pazarama.svg",
  shopify: "/integrations/shopify.svg",
  ikas: "/integrations/ikas.svg",
  shopier: "/integrations/shopier.svg",
};

function PlatformBadge({ platform, title }) {
  const src = PROVIDER_LOGOS[platform];
  const fallback = (platform || "?").slice(0, 2).toUpperCase();
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/80 bg-card p-1 shadow-sm" title={title || platform}>
      {src ? (
        <img src={src} alt={platform} className="h-full w-full object-contain" loading="lazy" />
      ) : (
        <span className="text-[9px] font-semibold text-muted-foreground">{fallback}</span>
      )}
    </span>
  );
}

export default function ProductStockListPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [savingCellKey, setSavingCellKey] = useState("");
  const perPage = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/parasut/product-stock?page=${page}&per_page=${perPage}&search=${encodeURIComponent(search)}`);
      setProducts(data.products || []);
      setIntegrations(data.integrations || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Ürün stok listesi yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / perPage) || 1;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  // Get stock for a specific integration
  const getIntegrationStock = (product, integrationId) => {
    const match = (product.integration_stocks || []).find(s => s.integration_id === integrationId);
    return match || null;
  };

  const beginEdit = (productId, integrationId, currentValue) => {
    setEditingCell({
      productId: String(productId),
      integrationId: String(integrationId),
      value: String(Math.max(0, Number(currentValue || 0))),
    });
  };

  const saveIntegrationStock = async (productId, integrationId) => {
    if (!editingCell) return;
    const cellKey = `${productId}:${integrationId}`;
    if (savingCellKey === cellKey) return;
    const parsed = Number(editingCell.value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Geçerli bir stok değeri girin");
      return;
    }
    const desired = Math.floor(parsed);
    setSavingCellKey(cellKey);
    try {
      await api.post("/stock/channel-set", {
        product_id: String(productId),
        integration_id: String(integrationId),
        stock: desired,
      });
      setProducts((prev) =>
        prev.map((p) => {
          if (String(p.id) !== String(productId)) return p;
          const nextStocks = [...(p.integration_stocks || [])];
          const idx = nextStocks.findIndex((x) => String(x.integration_id) === String(integrationId));
          if (idx >= 0) {
            nextStocks[idx] = { ...nextStocks[idx], stock: desired, active: desired > 0 };
          } else {
            nextStocks.push({ integration_id: String(integrationId), stock: desired, active: desired > 0 });
          }
          return { ...p, integration_stocks: nextStocks };
        })
      );
      toast.success("Platform stoğu güncellendi");
      setEditingCell(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Bilinmeyen hata";
      toast.error(`Platform stoğu güncellenemedi: ${detail}`);
    } finally {
      setSavingCellKey("");
    }
  };

  return (
    <div className="page-shell space-y-5" data-testid="product-stock-page">
      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title" data-testid="product-stock-title">Ürün Stok Listesi</h1>
          <p className="app-page-subtitle">Platform bazında stok miktarlarını görün ve düzenleyin.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Ürün adı, stok kodu veya barkod ara..."
          className="flex-1 bg-transparent text-sm outline-none"
          data-testid="product-stock-search"
        />
        {!loading && total > 0 && (
          <span className="text-xs text-muted-foreground shrink-0">{total} ürün</span>
        )}
      </div>

      <div className="table-shell overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Stok Durumu</h2>
            {!loading && <p>{total} ürün</p>}
          </div>
        </div>
      <div className="overflow-auto bg-card/70">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Ürünler yükleniyor...</span>
          </div>
        ) : products.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Package className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Ürün bulunamadı</p>
            </div>
          </div>
        ) : (
          <>
          <div className="space-y-2 p-3 md:hidden">
            {products.map((product) => (
              <div key={`mobile-${product.id}`} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-start gap-3">
                  {product.image_url ? (
                    <img src={product.image_url} alt="" className="h-11 w-11 rounded-lg object-cover border shrink-0" />
                  ) : (
                    <div className="h-11 w-11 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0">
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">{product.name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Stok: {product.stock} · {product.product_type || "Basit Ürün"}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {integrations.map((integ) => {
                    const integStock = getIntegrationStock(product, integ.id);
                    return (
                      <div key={`mobile-stock-${product.id}-${integ.id}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 text-[11px]">
                        <PlatformBadge platform={integ.platform} title={`#${integ.id} ${integ.name}`} />
                        <span className="font-semibold">{integStock ? integStock.stock : "-"}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/panel/urunler/${product.id}`)}
                    className="h-8 w-full gap-1.5 text-xs"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Görüntüle
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block">
          <table className="min-w-[1120px] w-full text-sm" data-testid="product-stock-table">
            <thead className="sticky top-0 z-10 bg-muted/30 border-b border-border">
              <tr>
                <th className="w-10 px-3 py-3 text-left">
                  <Checkbox
                    checked={selectedIds.size === products.length && products.length > 0}
                    onCheckedChange={toggleAll}
                    data-testid="stock-select-all"
                  />
                </th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Ürün</th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap"></th>
                <th className="px-3 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Stok</th>
                {integrations.map(integ => (
                  <th key={integ.id} className="px-2 py-3 text-center">
                    <div className="flex items-center justify-center" title={`#${integ.id} ${integ.name}`}>
                      <PlatformBadge platform={integ.platform} title={`#${integ.id} ${integ.name}`} />
                    </div>
                  </th>
                ))}
                <th className="w-24 px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {products.map(product => (
                <tr
                  key={product.id}
                  className={cn(
                    "hover:bg-muted/50 transition-colors",
                    selectedIds.has(product.id) && "bg-primary/5"
                  )}
                  data-testid={`stock-row-${product.id}`}
                >
                  {/* Checkbox */}
                  <td className="px-3 py-3">
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={() => toggleSelect(product.id)}
                    />
                  </td>
                  {/* Product Info */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt=""
                          className="w-11 h-11 rounded-xl object-cover border border-border/60 shrink-0 shadow-sm"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-xl bg-muted/70 border border-border/60 flex items-center justify-center shrink-0">
                          <ImageIcon className="h-4 w-4 text-muted-foreground/60" />
                        </div>
                      )}
                      <div className="min-w-0 max-w-[320px]">
                        <button
                          onClick={() => navigate(`/panel/urunler/${product.id}`)}
                          className="text-left group"
                          data-testid={`stock-product-name-${product.id}`}
                        >
                          <span className="font-semibold text-[13px] text-foreground group-hover:text-[#1487a3] transition-colors line-clamp-1" title={product.name}>
                            {product.name}
                          </span>
                        </button>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {product.sku && (
                            <span className="inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 whitespace-nowrap">
                              {product.sku}
                            </span>
                          )}
                          {product.barcode && (
                            <span className="inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 whitespace-nowrap">
                              {product.barcode}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Product Type */}
                  <td className="px-3 py-3 text-left">
                    <span className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-1 text-[11px] font-medium text-foreground/80 whitespace-nowrap">
                      {product.product_type || "Basit Ürün"}
                    </span>
                  </td>
                  {/* Total Stock */}
                  <td className="px-3 py-3 text-center">
                    <span className={cn(
                      "font-bold tabular-nums",
                      product.stock === 0 ? "text-red-500" : product.stock < 10 ? "text-amber-600" : "text-foreground"
                    )} data-testid={`stock-count-${product.id}`}>
                      {product.stock}
                    </span>
                  </td>
                  {/* Integration Stock Columns */}
                  {integrations.map(integ => {
                    const integStock = getIntegrationStock(product, integ.id);
                    const isEditing =
                      editingCell &&
                      String(editingCell.productId) === String(product.id) &&
                      String(editingCell.integrationId) === String(integ.id);
                    const cellKey = `${product.id}:${integ.id}`;
                    const isSaving = savingCellKey === cellKey;
                    return (
                      <td key={integ.id} className="px-2 py-3 text-center">
                        {integStock ? (
                          isEditing ? (
                            <div className="relative inline-flex items-center justify-center gap-1">
                              <Input
                                autoFocus
                                value={editingCell.value}
                                onChange={(e) => setEditingCell((prev) => prev ? ({ ...prev, value: e.target.value.replace(/[^\d]/g, "") }) : prev)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveIntegrationStock(product.id, integ.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    setEditingCell(null);
                                  }
                                }}
                                onBlur={() => saveIntegrationStock(product.id, integ.id)}
                                className="h-7 w-16 text-center text-xs font-semibold tabular-nums"
                                disabled={isSaving}
                              />
                              {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="relative inline-flex items-center justify-center rounded px-1 py-0.5 hover:bg-muted transition-colors"
                              onClick={() => beginEdit(product.id, integ.id, integStock.stock)}
                              title="Düzenlemek için tıklayın"
                            >
                              <span className={cn(
                                "text-xs font-bold tabular-nums",
                                integStock.active === false ? "text-red-500" : "text-foreground"
                              )}>
                                {integStock.stock}
                              </span>
                              <span className={cn(
                                "absolute -top-0.5 -right-2 w-1.5 h-1.5 rounded-full",
                                integStock.active === false ? "bg-red-500" : "bg-emerald-500"
                              )} title={integStock.active === false ? "Satışa Kapalı" : "Satışa Açık"} />
                            </button>
                          )
                        ) : (
                          <span className="text-muted-foreground/30">-</span>
                        )}
                      </td>
                    );
                  })}
                  {/* Action */}
                  <td className="px-3 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/panel/urunler/${product.id}`)}
                      className="h-8 gap-1.5 text-xs"
                      data-testid={`stock-view-${product.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Görüntüle
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {!loading && products.length > 0 && (
        <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-muted-foreground" data-testid="product-stock-pagination">
          <span>{total} ürün · Sayfa {page} / {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="stock-prev-page">Önceki</Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="stock-next-page">Sonraki</Button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
